import { useSyncStatus } from 'components/SyncDataComponent';
import { useNetworkStatus } from 'containers/NetworkStatusContainer';
import { format } from 'date-fns';
import React from 'react';
import {
  useQueryGetBookingSheets,
  useQueryGetBookingUnset,
  useQueryGetBookings,
} from 'repositories/booking/booking.query';
import { useQueryGetOutletStylists } from 'repositories/outlet';
import { useQueryGetTransactions } from 'repositories/transaction';

interface Props {
  outletId: string;
  date: Date;
}

export default function useGetBookingTransactions({
  outletId,
  date: rawDate,
}: Props) {
  const { isSyncing } = useSyncStatus();
  const { mode: isOnline } = useNetworkStatus();
  const date = format(rawDate, 'yyyy-MM-dd');

  // --- Single load queries ---
  const [loadAllBookings, allBookings] = useQueryGetBookings(outletId, date);

  const [loadAllTransactions, allTransactions] = useQueryGetTransactions(
    outletId,
    date,
  );

  // --- Booking management: Tab Manajemen ---
  const [loadBookingSheets, bookingSheets] = useQueryGetBookingSheets(
    outletId,
    date,
  );
  const [loadBookingUnset, bookingUnset] = useQueryGetBookingUnset(
    outletId,
    date,
  );

  // --- Stylist retrieval ---
  const [loadStylists, getStylists] = useQueryGetOutletStylists(outletId, date);

  // --- Derived Booking query results via client-side filters ---
  const bookings = React.useMemo(() => {
    const rawData = allBookings.data?.bookings.data || [];
    const filtered = rawData.filter(
      (booking) =>
        [1, 2, 3].includes(booking.status) &&
        booking.isWithoutTransaction === true,
    );
    return {
      ...allBookings,
      data: allBookings.data ? { bookings: { data: filtered } } : undefined,
    } as any;
  }, [allBookings]);

  const activeBookings = React.useMemo(() => {
    const rawData = allBookings.data?.bookings.data || [];
    const filtered = rawData.filter((booking) =>
      [1, 2].includes(booking.status),
    );
    return {
      ...allBookings,
      data: allBookings.data ? { bookings: { data: filtered } } : undefined,
    } as any;
  }, [allBookings]);

  const transactionBookings = React.useMemo(() => {
    const rawData = allBookings.data?.bookings.data || [];
    const filtered = rawData.filter(
      (booking) =>
        [1, 2, 3].includes(booking.status) &&
        booking.isSet === true &&
        booking.isWithoutTransaction === true,
    );
    return {
      ...allBookings,
      data: allBookings.data ? { bookings: { data: filtered } } : undefined,
    } as any;
  }, [allBookings]);

  const completedBookings = React.useMemo(() => {
    const rawData = allBookings.data?.bookings.data || [];
    const filtered = rawData.filter((booking) =>
      [3, 9].includes(booking.status),
    );
    return {
      ...allBookings,
      data: allBookings.data ? { bookings: { data: filtered } } : undefined,
    } as any;
  }, [allBookings]);

  // --- Derived Transaction query results via client-side filters ---
  const completedTransaction = React.useMemo(() => {
    const rawData = allTransactions.data?.transactions.data || [];
    const filtered = rawData.filter((tx) => tx.status === 2);
    return {
      ...allTransactions,
      data: allTransactions.data
        ? {
            transactions: {
              ...allTransactions.data.transactions,
              data: filtered,
            },
          }
        : undefined,
    } as any;
  }, [allTransactions]);

  const cancelledTransaction = React.useMemo(() => {
    const rawData = allTransactions.data?.transactions.data || [];
    const filtered = rawData.filter((tx) => [8, 9].includes(tx.status));
    return {
      ...allTransactions,
      data: allTransactions.data
        ? {
            transactions: {
              ...allTransactions.data.transactions,
              data: filtered,
            },
          }
        : undefined,
    } as any;
  }, [allTransactions]);

  // Load everything on mount, but wait until sync data is done.
  // Each query gated only by its OWN `.called` flag.
  // This guarantees all data needed by both Booking and Transaction screens
  // gets fetched (and cached) regardless of which screen is opened first,
  // so offline mode always has something to fall back on.
  React.useEffect(() => {
    if (!outletId || isSyncing) return;

    if (!allBookings.called) loadAllBookings();
    if (!allTransactions.called) loadAllTransactions();

    if (!bookingSheets.called) loadBookingSheets();
    if (!bookingUnset.called) loadBookingUnset();

    if (!getStylists.called) loadStylists();
  }, [
    outletId,
    isSyncing,
    allBookings.called,
    allTransactions.called,
    bookingSheets.called,
    bookingUnset.called,
    getStylists.called,
    loadAllBookings,
    loadAllTransactions,
    loadBookingSheets,
    loadBookingUnset,
    loadStylists,
  ]);

  return {
    bookings,
    completedTransaction,
    cancelledTransaction,
    getStylists,
    completedBookings,
    bookingSheets,
    bookingUnset,
    activeBookings,
    transactionBookings,
  };
}
